// External Dependencies:
import { showLoading, hideLoading } from 'react-redux-loading-bar'
import i18n from 'i18n-calypso'
import Cookie from 'js-cookie'

// Internal Dependencies:
import { doRequest } from 'church-theme-library/utilities/request'
import { getQuarter } from 'church-theme-library/utilities/date'

export function getScreenSize() {
	return {
		type: 'GET_SCREEN_SIZE'
	}
}

/**
 * Scripts
 */
export function addScript( value ) {
	return {
		type: 'ADD_SCRIPT',
		payload: value
	}
}

/**
 * GDPR
 */
export function setCookieConsent( value ) {
	return {
		type: 'SET_COOKIE_CONSENT',
		payload: value
	}
}

/**
 * Network
 */
export function setOnLineStatus( value ) {
	return {
		type: 'SET_ONLINE_STATUS',
		payload: value
	}
}

export function setConnectionStatus( value ) {
	return {
		type: 'SET_CONNECTION_STATUS',
		payload: value
	}
}

/**
 * Images
 */
export function registerImageAsLoaded( url ) {
	return function( dispatch, getState ) {
		let loadedImages = localStorage.getItem( 'loadedImages' )

		if ( loadedImages ) {
			loadedImages = JSON.parse( loadedImages )
		} else {
			loadedImages = []
		}

		if ( url && ! loadedImages.includes( url ) ) {
			loadedImages = [
				...loadedImages,
				url
			]

			localStorage.setItem( 'loadedImages', JSON.stringify( loadedImages ) )
		}

		return dispatch( {
			type: 'REGISTER_IMAGE_ID_AS_LOADED',
			payload: loadedImages
		} )
	}
}

/**
 * Locale
 */
export function setLocaleString( locale ) {
	Cookie.set( 'locale', locale )

	return {
		type: 'SET_LOCALE',
		payload: locale
	}
}

export function setLocale( locale ) {
	return function( dispatch, getState ) {
		// Short circuit for existing data:
		if ( locale == getState().site.locale ) {
			return
		}

		// Get locale json file:
		if ( 'en' === locale ) {
			// Use default english text:
			i18n.setLocale( false )
			dispatch( setLocaleString( locale ) )
		} else {
			doRequest( `${siteData.languages_url}${locale}.json?v=${siteData.version}` )
				.then( json => {
					// Load the locale.
					i18n.setLocale( json )
					dispatch( setLocaleString( locale ) )
				})
				.catch( error => {

				})
		}

		// Get localised site data:
		doRequest( `${siteData.api_base_url}site-data/?locale=${locale}&v=${siteData.version}` )
			.then( json => {
				dispatch( receiveSiteData( json ) )
			})
			.catch( error => {

			})
	}
}

/**
 * Announcement bar
 */
export function dismissAnnouncementBar() {
	return {
		type: 'DISMISS_ANNOUNCEMENT_BAR'
	}
}

/**
 * Site data
 */
export function getSiteData( locale = false ) {
	return function( dispatch, getState ) {
		if ( ! locale ) {
			locale = getState().site.locale
		}

		doRequest( `${siteData.api_base_url}site-data/?locale=${locale}&v=${siteData.version}` )
			.then( json => {
				dispatch( receiveSiteData( json ) )
			})
			.catch( error => {

			})
	}
}

export function receiveSiteData( data ) {
	return {
		type: 'RECEIVE_SITE_DATA',
		payload: data
	}
}

/**
 * Sabbath school data
 */
export function getSabbathSchoolData( locale = false) {
	return function( dispatch, getState ) {
		const { locale, sabbathSchoolThisWeekByLocale, currentYear } = getState().site

		if ( ! locale ) {
			locale = getState().site.locale
		}

		if ( sabbathSchoolThisWeekByLocale && sabbathSchoolThisWeekByLocale[ locale ] ) {
			dispatch( receiveSabbathSchoolDataSingle( sabbathSchoolThisWeekByLocale[ locale ] ) )

			return // Only load once.
		}

		let quarter = getQuarter()

		doRequest( `${siteData.manage_api_base_url}sabbath-school?all_in_quarter=true&year=${currentYear}&quarter=${quarter}&locale=${locale}` )
			.then( json => {
				dispatch( receiveSabbathSchoolData( json ) )
			} )
			.catch( error => {

			} )
	}
}

export function receiveSabbathSchoolData( data ) {
	return {
		type: 'RECEIVE_SABBATH_SCHOOL_DATA',
		payload: data
	}
}

export function receiveSabbathSchoolDataSingle( data ) {
	return {
		type: 'RECEIVE_SABBATH_SCHOOL_DATA_SINGLE',
		payload: data
	}
}

/**
 * Menu
 */
export function openMenu() {
	return {
		type: 'OPEN_MENU'
	}
}

export function closeMenu() {
	return {
		type: 'CLOSE_MENU'
	}
}

export function setMenuOpenState( payload ) {
	return {
		type: 'SET_MENU_OPEN_STATE',
		payload
	}
}

export function setLocaleMenuOpenState( payload ) {
	return {
		type: 'SET_LOCALE_MENU_OPEN_STATE',
		payload
	}
}

export function setMenuOverflowItems( payload ) {
	return {
		type: 'SET_MENU_OVERFLOW_ITEMS',
		payload
	}
}

/**
 * Login
 */
export function login() {
	return {
		type: 'LOGIN'
	}
}

export function loginDisabled( value ) {
	return {
		type: 'LOGIN_DISABLED',
		payload: value
	}
}

export function loginProcessing() {
	return {
		type: 'LOGIN_PROCESSING'
	}
}

export function loginProcessed() {
	return {
		type: 'LOGIN_PROCESSED'
	}
}

export function loginError() {
	return {
		type: 'LOGIN_ERROR'
	}
}

export function loginSuccess() {
	return {
		type: 'LOGIN_SUCCESS'
	}
}

export function loginMessage( message ) {
	return {
		type: 'LOGIN_MESSAGE',
		payload: message
	}
}

export function logout() {
	return {
		type: 'LOGOUT'
	}
}

export function submitLogin( username, password ) {
	return function( dispatch ) {
		dispatch( loginDisabled( true ) )
		dispatch( loginProcessing() )

		return doRequest( `${siteData.api_base_url}/login/?username=${username}&password=${password}` )
			.then( json => {
				dispatch( loginProcessed() )

				// add a delay for UX
				setTimeout( () => {
					dispatch( loginSuccess() )

					setTimeout( () => {
						dispatch( loginDisabled( false ) )
						dispatch( login() )
					}, 1000 )
				}, 1000 )
			})
			.catch( function( response ) {
				dispatch( loginProcessed() )

				// add a delay for UX
				setTimeout( () => {
					dispatch( loginError() )

					setTimeout( () => {
						dispatch( loginDisabled( false ) )
					}, 1000 )
				}, 1000 )
			})
	}
}

/**
 * Menu
 */
export function requestMenuData() {
	return {
		type: 'REQUEST_MENU_DATA'
	}
}

export function getMenuData( username, password ) {
	return function( dispatch, getState ) {
		const { isLoadingMenu } = getState().site

		// Don't load the same thing simultaneously:
		if ( isLoadingMenu ) {
			return
		}

		dispatch( requestMenuData() )

		const { locale } = getState().site

		return doRequest( siteData.api_base_url + 'menu/?locale=' + locale + '&v=' + siteData.version )
			.then( json => {
				dispatch( receiveMenuData( json ) )
			})
			.catch( error => {

			})
	}
}

export function receiveMenuData( data ) {
	return {
		type: 'RECEIVE_MENU_DATA',
		payload: data
	}
}

/**
 * Media
 */
export function getVideosData() {
	return function( dispatch, getState ) {
		const { videosReceived } = getState().site

		if ( videosReceived ) {
			return
		}

		return doRequest( siteData.api_base_url + 'videos' )
			.then( json => {
				dispatch( receiveVideosData( json ) )
			} )
			.catch( error => {

			} )
	}
}

export function getLivestreamData() {
	return function( dispatch, getState ) {
		const { livestreamReceived } = getState().site

		if ( livestreamReceived ) {
			return
		}

		return doRequest( siteData.api_base_url + 'livestream' )
			.then( json => {
				dispatch( receiveLivestreamData( json ) )
			} )
			.catch( error => {

			} )
	}
}

export function receiveVideosData( data ) {
	return {
		type: 'RECEIVE_VIDEOS_DATA',
		payload: data
	}
}

export function receiveLivestreamData( data ) {
	return {
		type: 'RECEIVE_LIVESTREAM_DATA',
		payload: data
	}
}
